import { useState, useRef, useEffect } from 'react';
import { cva } from 'class-variance-authority';
import DropdownItem from '@/components/DropdownItem';
import type { KeyboardEvent } from 'react';
import type { ReactNode } from 'react';
import type { Option } from '@/types/dropdown.types';

interface DropdownProps {
  header?: string | ReactNode;
  dataCy?: string;
  options: Option[];
  dropdownClassName?: string;
  toggleButtonContent: ReactNode;
  onToggleButtonClick?: (isOpen: boolean) => void;
  toggleButtonVariant?: 'text' | 'navigation' | 'default';
}

function Dropdown({
  header,
  dataCy,
  options,
  toggleButtonContent,
  onToggleButtonClick,
  toggleButtonVariant = 'default',
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownId = `dropdown-${Math.random().toString(36).substring(2, 11)}`;

  useEffect(() => {
    if (onToggleButtonClick) {
      onToggleButtonClick(isOpen);
    }
  }, [isOpen, onToggleButtonClick]);

  function handleClickOutside(event: MouseEvent) {
    if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  }

  function toggleDropdown() {
    setIsOpen(!isOpen);
  }

  function closeDropdown() {
    setIsOpen(false);
  }

  function handleMouseEnter() {
    setIsOpen(true);
  }

  function handleMouseLeave() {
    setIsOpen(false);
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      setIsOpen(false);
      buttonRef.current?.focus();
    }
  }

  function handleButtonKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsOpen(!isOpen);
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const buttonClasses = cva('flex font-medium justify-center items-center', {
    variants: {
      variant: {
        text: 'p-3 py-2 hover:bg-gray-100 rounded-sm',
        navigation: '-mb-2.5 pt-2 p-0 !items-stretch rounded-md font-semibold',
        default: 'text-white h-10 w-10 bg-[#002327] hover:opacity-80 rounded-lg',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  });

  return (
    <div
      data-cy={dataCy}
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className='relative inline-block h-10'
    >
      <button
        ref={buttonRef}
        aria-haspopup='true'
        aria-expanded={isOpen}
        aria-controls={dropdownId}
        aria-label='Toggle dropdown menu'
        onKeyDown={handleButtonKeyDown}
        onClick={toggleDropdown}
        className={buttonClasses({ variant: toggleButtonVariant })}
      >
        {toggleButtonContent}
      </button>

      <div className='pt-2'>
        <div
          role='menu'
          id={dropdownId}
          ref={dropdownRef}
          onKeyDown={handleKeyDown}
          className={`${isOpen ? 'block' : 'hidden'} absolute right-0 z-50 min-w-40 bg-white border border-gray-200 rounded-lg shadow-lg border-t`}
        >
          {header && <header className='mb-1 px-4 py-2 font-semibold border-b border-gray-100'>{header}</header>}
          <ul className='max-h-60 overflow-y-auto'>
            {options.map((option, index) => (
              <li
                key={index}
                role='menuitem'
                onClick={closeDropdown}
                aria-label={option.label}
                className={`${option.dividerAbove ? 'border-t mt-1' : ''}`}
              >
                <div className={`${option.dividerAbove ? 'mt-1' : ''}`}>
                  <DropdownItem
                    option={option}
                    closeDropdown={closeDropdown}
                    hoverRoundedTop={!header && index === 0}
                    hoverRoundedBottom={index === options.length - 1}
                    className={toggleButtonVariant === 'navigation' ? 'font-semibold' : ''}
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default Dropdown;
